/* eslint-disable react/jsx-props-no-spreading */
import React, { Suspense, useEffect, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useForm, useWatch } from 'react-hook-form';
import { To } from 'react-router';

import { Button } from 'shared/components/Button/Button';
import { TextInput } from 'shared/components/inputs/TextInput/TextInput';
import { handlePasswordValidation } from 'modules/auth/Auth.utils';
import { MINIMUM_PASSWORD_LENGTH } from 'constants/auth';
import { getScoreWords } from 'modules/auth/components/PasswordForm/getScoreWords';
import { LoadingSpinner } from 'shared/components/LoadingSpinner';
import { ContactInfoBodyHeader, ContentBody } from '../ContactInfoBody/ContactInfoBody.styles';
import { Name } from '../AccountBody/AccountBody.styles';
import { BodyText, FormStyled, Label, LinkStyled, Value } from './PasswordFormBody.styles';

const PasswordStrengthBar = React.lazy(() => import('react-password-strength-bar'));

export interface IFormInput {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

type Props = {
  email: string;
  onSubmit: (formData: IFormInput) => void;
  is400Error: boolean;
  supportPath: To;
};

export function PasswordFormBody({ email, onSubmit, is400Error, supportPath }: Props) {
  const [passwordScore, setPasswordScore] = useState(0);

  const { t } = useTranslation();

  const {
    control,
    register,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<IFormInput>();
  const watchNewPassword = useWatch({
    control,
    name: 'newPassword',
  });

  const scoreWords = getScoreWords(t);
  const watchOldPassword = useWatch({
    control,
    name: 'oldPassword',
  });

  useEffect(() => {
    if (is400Error) {
      setError('oldPassword', {
        type: 'manual',
        message: t('account.currentPasswordIncorrect'),
      });
    }
  }, [is400Error, setError, t]);

  return (
    <ContentBody>
      <ContactInfoBodyHeader>
        <Name as="h3">{t('account.password')}</Name>
      </ContactInfoBodyHeader>

      <FormStyled onSubmit={handleSubmit(onSubmit)}>
        <BodyText as="p">
          <Trans
            i18nKey="account.contactSupport2"
            components={{
              a: <LinkStyled to={supportPath} />,
            }}
          />
        </BodyText>

        <div>
          <Label as="p">{t('account.email')}</Label>
          <Value as="p">{email}</Value>
        </div>

        <TextInput
          id="password-form-old-password"
          ariaLabel={t('account.oldPassword')}
          type="password"
          label={t('account.oldPassword')}
          placeholder=" "
          hasError={Boolean(errors.oldPassword)}
          errorMessage={errors.oldPassword?.message}
          {...register('oldPassword', { required: t('account.passwordRequired') })}
        />

        <div>
          <TextInput
            id="password-form-new-password"
            ariaLabel={t('account.newPassword')}
            type="password"
            label={t('account.newPassword')}
            placeholder=" "
            hasError={Boolean(errors.newPassword)}
            errorMessage={errors.newPassword?.message}
            {...register('newPassword', {
              validate: (value) =>
                handlePasswordValidation({
                  value,
                  newPassword: watchNewPassword,
                  passwordScore,
                  translator: t,
                  isNewPasswordField: true,
                  oldPassword: watchOldPassword,
                }),
            })}
          />

          <Suspense fallback={<LoadingSpinner />}>
            <PasswordStrengthBar
              password={watchNewPassword}
              minLength={MINIMUM_PASSWORD_LENGTH}
              shortScoreWord={watchNewPassword ? t('passwordStrength.tooWeak') : ''}
              scoreWords={scoreWords}
              onChangeScore={(score) => {
                setPasswordScore(score);
              }}
            />
          </Suspense>
        </div>

        <TextInput
          id="password-form-confirm-password"
          ariaLabel={t('account.confirmPassword')}
          label={t('account.confirmPassword')}
          type="password"
          placeholder=" "
          hasError={Boolean(errors.confirmPassword)}
          errorMessage={errors.confirmPassword?.message}
          {...register('confirmPassword', {
            validate: (value) =>
              handlePasswordValidation({
                value,
                newPassword: watchNewPassword,
                passwordScore,
                translator: t,
                isConfirmPasswordField: true,
              }),
          })}
        />

        <Button type="submit" label="Save" isFullWidth margin="24px 0 0" />
      </FormStyled>
    </ContentBody>
  );
}

export default PasswordFormBody;
